/**
 * Table column definitions for the buddy group list.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import type { BuddyGroupSummary, BuddyGroupStatus } from '../types/buddy-group';

const statusVariant: Record<BuddyGroupStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  pending: 'secondary',
  locked: 'outline',
  disbanded: 'destructive',
};

interface ColumnActions {
  onView: (group: BuddyGroupSummary) => void;
  onEdit: (group: BuddyGroupSummary) => void;
  onDissolve: (group: BuddyGroupSummary) => void;
  canEdit: boolean;
  canDissolve: boolean;
}

export function getBuddyGroupColumns(actions: ColumnActions) {
  return [
    {
      key: 'group_name',
      header: 'Group Name',
      render: (row: BuddyGroupSummary) => (
        <span className="font-medium">{row.group_name || '(unnamed)'}</span>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      render: (row: BuddyGroupSummary) => (
        <span className="text-sm text-muted-foreground">
          {row.member_count} member{row.member_count !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'primary_cardholder',
      header: 'Primary Cardholder',
      render: (row: BuddyGroupSummary) => (
        <span className="text-sm">
          {row.primary_member_name ?? <span className="text-muted-foreground italic">none</span>}
        </span>
      ),
    },
    {
      key: 'registrations',
      header: 'Entries',
      render: (row: BuddyGroupSummary) => (
        <span className="text-sm tabular-nums">{row.registration_count}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: BuddyGroupSummary) => (
        <Badge variant={statusVariant[row.status]}>{row.status}</Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row: BuddyGroupSummary) => (
        <div className="flex items-center gap-1 justify-end">
          <Button variant="ghost" size="sm" onClick={() => actions.onView(row)} title="View">
            <Eye className="h-4 w-4" />
          </Button>
          {actions.canEdit && (
            <Button variant="ghost" size="sm" onClick={() => actions.onEdit(row)} title="Edit">
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {actions.canDissolve && (
            <Button variant="ghost" size="sm" onClick={() => actions.onDissolve(row)} title="Dissolve" className="text-destructive hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];
}
